import { DataTable, Select } from '@boltenergy-be/design-system'
import { InvoicesTableProps } from './types.ts'
import styles from './InvoiceTable.module.scss'
import { useState } from 'react'
import { paginate, sortRowsByKey } from 'pages/app/billing/invoices/utils.ts'
import StackedInvoicesView from './stacked-invoices-view/StackedInvoicesView.tsx'
import { SortingDirection } from '@boltenergy-be/design-system/dist/components/data-table/types'
import { Pagination } from '@boltenergy-be/design-system'
import { InvoiceCells, InvoiceTableTabs } from 'pages/app/billing/invoices/table-data/types.ts'
import useWindowSize from 'hooks/useWindowSize.tsx'
import classNames from 'classnames'
import { UserTypes } from 'store/auth/types.ts'
import { useStoreSelector } from 'hooks/store.ts'
import { DEFAULT_ITEMS_PER_PAGE } from 'pages/app/billing/invoices/constants.ts'

const InvoicesTable = ({ tableData, activeTab, currentTablePage, onPageChange, onClickRow, ref }: InvoicesTableProps) => {
  // Redux
  const { userType } = useStoreSelector((store) => store.auth)
  const isSuperuser = userType === UserTypes.SUPER_USER

  // Window size
  const { isMobile } = useWindowSize()

  // state
  const [sorting, setSorting] = useState<{ key: InvoiceCells; direction: SortingDirection }>({
    key: InvoiceCells.DATE,
    direction: 'desc'
  })
  const [itemsPerPage, setItemsPerPage] = useState<number>(DEFAULT_ITEMS_PER_PAGE)

  // constants
  const pages: InvoicesTableProps['tableData'][] = paginate(tableData.sort(sortRowsByKey(sorting?.key, sorting?.direction)), itemsPerPage)
  const activeTablePage = Math.min(currentTablePage, pages.length)
  const selectedDataset = pages[activeTablePage - 1]
  const canChangeItemsPerPage = tableData.length > DEFAULT_ITEMS_PER_PAGE && isSuperuser
  const canPaginate = pages.length > 1

  /**
   * Handle the row click
   * @param {number} index
   */
  const handleClickRow = (index: number) => onClickRow(selectedDataset[index].id)

  /**
   * Calculates the items per page options based on the table data length
   * @returns {number[]}
   */
  const calculateItemsPerPageOptions = (): number[] => {
    const options: number[] = [10, 25]
    const extraOptions: number[] = [50, 100, 150, 200]

    extraOptions.forEach((option) => {
      const lastOption = options[options.length - 1]
      if (tableData.length > lastOption) {
        options.push(option)
      }
    })

    return options
  }

  return (
    <section ref={ref}>
      {isMobile ? (
        <StackedInvoicesView data={selectedDataset} handleClickRow={handleClickRow} />
      ) : (
        <DataTable
          {...{ sorting }}
          hideHeader={![InvoiceTableTabs.SUPER_USER_CUSTOMER, InvoiceTableTabs.SUPER_USER_SUPPLIER].includes(activeTab)}
          rows={selectedDataset}
          className={classNames(styles.table, styles[activeTab])}
          sortableKeys={isSuperuser ? Object.values(InvoiceCells) : [InvoiceCells.DATE]}
          onSort={({ key, direction }: { key: InvoiceCells; direction: SortingDirection }) => setSorting({ key, direction })}
          handleClickRow={handleClickRow}
        />
      )}
      {(canPaginate || canChangeItemsPerPage) && (
        <footer className={styles['table-footer']}>
          {canPaginate && (
            <Pagination pagesLength={pages.length} activePageNumber={activeTablePage} onPageChange={onPageChange} maxWidth={320} />
          )}

          {canChangeItemsPerPage && (
            <form className={styles['items-per-page-form']}>
              <Select
                className={styles.select}
                layoutClassName={styles['select-layout']}
                label="Items per page:"
                value={itemsPerPage}
                onChange={(e) => {
                  onPageChange(1)
                  setItemsPerPage(Number(e.target.value))
                }}
              >
                {calculateItemsPerPageOptions().map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            </form>
          )}
        </footer>
      )}
    </section>
  )
}

export default InvoicesTable
